import { PDFDocument, StandardFonts, rgb } from 'pdf-lib'

const A4_W = 595.28
const A4_H = 841.89
const M = 36
const BLACK = rgb(0, 0, 0)
const GRAY_FILL = rgb(0.88, 0.88, 0.88)
const GRAY_STROKE = rgb(0.35, 0.35, 0.35)
/** Escala máxima respecto al área útil: imagen entera, un poco más chica (sin recortes ni franjas). */
const GALLERY_FIT_PAD = 0.98

function formatArsPdf(n) {
  const x = Number(n)
  const safe = Number.isFinite(x) ? x : 0
  const s = new Intl.NumberFormat('es-AR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  }).format(safe)
  return `$ ${s}`
}

function truncPrecioRep(val) {
  const n = Number(val)
  if (!Number.isFinite(n)) return 0
  const sign = n < 0 ? -1 : 1
  return (sign * Math.floor(Math.abs(n) * 100 + 1e-9)) / 100
}

function cantRep(r) {
  const q = Number(r?.cantidad)
  if (Number.isInteger(q) && q >= 1) return q
  return 1
}

async function fetchBytes(url) {
  const r = await fetch(url, { mode: 'cors' })
  if (!r.ok) throw new Error(`${r.status}`)
  return new Uint8Array(await r.arrayBuffer())
}

async function embedPdfImageFromBytes(pdfDoc, bytes, urlHint = '') {
  const u = String(urlHint || '').split('?')[0].toLowerCase()
  if (u.endsWith('.png')) return pdfDoc.embedPng(bytes)
  if (u.endsWith('.jpg') || u.endsWith('.jpeg') || u.endsWith('.jfif')) return pdfDoc.embedJpg(bytes)
  try {
    return await pdfDoc.embedPng(bytes)
  } catch {
    return pdfDoc.embedJpg(bytes)
  }
}

async function embedRemoteImage(pdfDoc, url) {
  const bytes = await fetchBytes(url)
  return embedPdfImageFromBytes(pdfDoc, bytes, url)
}

function loadHtmlImageFromBytes(bytes) {
  return new Promise((resolve, reject) => {
    const blob = new Blob([bytes])
    const ou = URL.createObjectURL(blob)
    const im = new Image()
    im.onload = () => {
      URL.revokeObjectURL(ou)
      resolve(im)
    }
    im.onerror = () => {
      URL.revokeObjectURL(ou)
      reject(new Error('Image load'))
    }
    im.src = ou
  })
}

function wrapLines(text, font, size, maxW) {
  const out = []
  const paragraphs = String(text || '').split(/\n/)
  for (let pi = 0; pi < paragraphs.length; pi += 1) {
    const para = paragraphs[pi]
    const words = para.split(/\s+/).filter(Boolean)
    let line = ''
    const flush = () => {
      if (line) out.push(line)
      line = ''
    }
    for (const w of words) {
      const test = line ? `${line} ${w}` : w
      if (font.widthOfTextAtSize(test, size) <= maxW) {
        line = test
      } else {
        if (line) flush()
        if (font.widthOfTextAtSize(w, size) <= maxW) line = w
        else {
          let chunk = ''
          for (let i = 0; i < w.length; i += 1) {
            const t2 = chunk + w[i]
            if (font.widthOfTextAtSize(t2, size) > maxW) {
              if (chunk) out.push(chunk)
              chunk = w[i]
            } else chunk = t2
          }
          line = chunk
        }
      }
    }
    flush()
    if (words.length === 0 && pi < paragraphs.length - 1) out.push('')
  }
  return out.length ? out : ['']
}

function drawGrayBar(page, x, yTop, w, h, fontBold, title, alignCenter) {
  const y = yTop - h
  page.drawRectangle({
    x,
    y,
    width: w,
    height: h,
    color: GRAY_FILL,
    borderColor: GRAY_STROKE,
    borderWidth: 0.6
  })
  const size = 8.5
  const tw = fontBold.widthOfTextAtSize(title, size)
  let tx = x + w - tw - 8
  if (alignCenter) tx = x + (w - tw) / 2
  const textBaselineY = y + h / 2 - size * 0.28
  page.drawText(title, {
    x: Math.max(x + 4, tx),
    y: textBaselineY,
    size,
    font: fontBold,
    color: BLACK
  })
  return y
}

function filterGallery(galleryImages, tipoUpper) {
  return (galleryImages || []).filter(
    (g) => String(g?.tipo_archivo || '').trim().toUpperCase() === tipoUpper
  )
}

/** Dimensiones en px del primer frame de una imagen (navegador). */
async function getImagePixelSize(bytes) {
  try {
    const bmp = await createImageBitmap(new Blob([bytes]))
    const w = bmp.width
    const h = bmp.height
    try {
      bmp.close()
    } catch {
      /* noop */
    }
    return { iw: w, ih: h }
  } catch {
    const im = await loadHtmlImageFromBytes(bytes)
    return { iw: im.naturalWidth, ih: im.naturalHeight }
  }
}

/**
 * Altura en puntos PDF de una foto de galería encajada entera (escala uniforme, sin recortes).
 */
async function measureFirstGallerySlabHeight(bytes, url, innerW, pageCapH, forzarAnchoCompleto) {
  const { iw, ih } = await getImagePixelSize(bytes)
  if (iw < 1 || ih < 1) return 0
  const maxBoxW = (forzarAnchoCompleto ? innerW : innerW - 8) * GALLERY_FIT_PAD
  const maxHDisponible = Math.min(pageCapH, A4_H - 2 * M - 40) * GALLERY_FIT_PAD
  const scale = Math.min(maxBoxW / iw, maxHDisponible / ih)
  return ih * scale
}

function createCursor(pdfDoc, font, fontBold) {
  const innerW = A4_W - 2 * M
  const state = {
    page: pdfDoc.addPage([A4_W, A4_H]),
    y: A4_H - M
  }

  function ensure(minBottom) {
    if (state.y - minBottom < M) {
      state.page = pdfDoc.addPage([A4_W, A4_H])
      state.y = A4_H - M
    }
  }

  return {
    get page() {
      return state.page
    },
    set page(p) {
      state.page = p
    },
    get y() {
      return state.y
    },
    setY(v) {
      state.y = v
    },
    bump(dy) {
      state.y -= dy
    },
    ensure,
    innerW,
    pdfDoc,
    font,
    fontBold
  }
}

/**
 * @param {object} opts
 * @param {string} opts.assetBaseUrl — sin slash final
 */
export async function pdfProformaGarantiaUnico(opts) {
  const assetBase = String(opts.assetBaseUrl || '').replace(/\/$/, '')
  const assetUrl = (name) => `${assetBase}/${encodeURIComponent(name)}`

  const bac = opts.bac ?? '279744'
  const concesionario = opts.concesionario ?? 'BORIGEN BETZEL SRL'
  const cliente = String(opts.cliente || '')
  const modelo = String(opts.modelo || '')
  const anio = String(opts.anio != null && opts.anio !== '' ? opts.anio : '—')
  const vin = String(opts.vin || '')
  const fechaDocumento = String(opts.fechaDocumento || '')
  const fechaAperturaOr = String(opts.fechaAperturaOr || '—')
  const numeroOrden = String(opts.numeroOrden != null ? opts.numeroOrden : '')
  const kmTexto = String(opts.kmTexto || '—')
  const patente = String(opts.patente || '')
  const horasMoLinea = String(opts.horasMoLinea || '—')
  const resumenDiagnostico = String(opts.resumenDiagnostico || '')
  const repuestosRaw = Array.isArray(opts.repuestos) ? opts.repuestos : []
  const inc = opts.includeSections || {}
  const galleryImages = Array.isArray(opts.galleryImages) ? opts.galleryImages : []

  const repuestos = repuestosRaw.map((r) => {
    const precioU = truncPrecioRep(r?.precio_estimado)
    const q = cantRep(r)
    return {
      cantidad: q,
      numero_parte: String(r?.numero_parte || '—'),
      descripcion: String(r?.descripcion_repuesto || r?.descripcion || '—'),
      precio_unit: precioU,
      subtotal: precioU * q
    }
  })

  const totalMonto = repuestos.reduce((a, r) => a + r.subtotal, 0)

  const pdfDoc = await PDFDocument.create()
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica)
  const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold)

  const c = createCursor(pdfDoc, font, fontBold)
  const { innerW } = c

  /** @param preloadedPng — PNG ya incrustado (p. ej. tras medir altura); evita doble fetch. */
  const drawAssetBanner = async (fileName, fallbackTitle, fallbackRight, preloadedPng = null) => {
    try {
      const png = preloadedPng ?? (await embedRemoteImage(pdfDoc, assetUrl(fileName)))
      const scale = innerW / png.width
      const h = png.height * scale
      c.ensure(h + 8)
      c.page.drawImage(png, { x: M, y: c.y - h, width: innerW, height: h })
      c.bump(h + 2)
    } catch {
      c.ensure(28)
      drawGrayBar(c.page, M, c.y, innerW, 22, fontBold, fallbackTitle, !!fallbackRight)
      c.bump(28)
    }
  }

  await drawAssetBanner(
    'datos_dealer_vehiculo_listado_piezas.png',
    'Datos Dealer y Vehículo – Listados de Piezas',
    false
  )

  const fsBox = 8
  const rowStep = 14
  const gutter = 8
  const midLine = M + innerW / 2
  const leftLabelX = M + gutter
  const rightLabelX = midLine + gutter / 2
  const leftLabels = ['BAC:', 'Concesionario:', 'Cliente:', 'Modelo:', 'Año:', 'VIN N°:']
  const rightLabels = [
    'Fecha:',
    'Fecha apertura OR:',
    'OR/OT N°:',
    'KM:',
    'Patente/Matrícula:',
    'Hs MO:'
  ]
  const leftVals = [bac, concesionario, cliente, modelo, anio, vin]
  const rightVals = [
    fechaDocumento,
    fechaAperturaOr,
    numeroOrden,
    kmTexto,
    patente,
    horasMoLinea
  ]

  let maxLeftLab = 0
  for (const lb of leftLabels) {
    maxLeftLab = Math.max(maxLeftLab, fontBold.widthOfTextAtSize(`${lb} `, fsBox))
  }
  let maxRightLab = 0
  for (const lb of rightLabels) {
    maxRightLab = Math.max(maxRightLab, fontBold.widthOfTextAtSize(`${lb} `, fsBox))
  }

  const leftValX = leftLabelX + maxLeftLab
  const rightValX = rightLabelX + maxRightLab
  const leftLineEnd = midLine - gutter
  const rightLineEnd = M + innerW - gutter
  const innerLeftW = leftLineEnd - leftValX - 2
  const innerRightW = rightLineEnd - rightValX - 2

  const truncateToWidth = (text, maxW) => {
    const s = String(text == null || text === '' ? '—' : text)
    if (font.widthOfTextAtSize(s, fsBox) <= maxW) return s
    let lo = 0
    let hi = s.length
    while (lo < hi) {
      const midIdx = Math.ceil((lo + hi) / 2)
      const t = `${s.slice(0, midIdx)}…`
      if (font.widthOfTextAtSize(t, fsBox) <= maxW) lo = midIdx
      else hi = midIdx - 1
    }
    return lo > 0 ? `${s.slice(0, lo)}…` : '…'
  }

  const boxH = 6 * rowStep + 24
  c.ensure(boxH + 12)
  c.page.drawRectangle({
    x: M,
    y: c.y - boxH,
    width: innerW,
    height: boxH,
    borderColor: BLACK,
    borderWidth: 1
  })

  const firstRowBaseline = c.y - 18
  for (let i = 0; i < 6; i += 1) {
    const baselineY = firstRowBaseline - i * rowStep
    const underlineY = baselineY - 2.5

    c.page.drawText(`${leftLabels[i]} `, {
      x: leftLabelX,
      y: baselineY,
      size: fsBox,
      font: fontBold
    })
    const lv = truncateToWidth(leftVals[i], innerLeftW)
    c.page.drawText(lv, { x: leftValX, y: baselineY, size: fsBox, font, color: BLACK })
    c.page.drawLine({
      start: { x: leftValX, y: underlineY },
      end: { x: leftLineEnd, y: underlineY },
      thickness: 0.45,
      color: BLACK
    })

    c.page.drawText(`${rightLabels[i]} `, {
      x: rightLabelX,
      y: baselineY,
      size: fsBox,
      font: fontBold
    })
    const rv = truncateToWidth(rightVals[i], innerRightW)
    c.page.drawText(rv, { x: rightValX, y: baselineY, size: fsBox, font, color: BLACK })
    c.page.drawLine({
      start: { x: rightValX, y: underlineY },
      end: { x: rightLineEnd, y: underlineY },
      thickness: 0.45,
      color: BLACK
    })
  }

  c.bump(boxH + 10)

  const colCant = M + 6
  const colPart = colCant + 36
  const colDesc = colPart + 78
  const colVu = colDesc + 188
  const colVt = colVu + 92
  /** Separación entre el borde derecho del texto monetario y la línea vertical (evita que el trazo corte el texto). */
  const moneyCellPad = 5
  const colVuTextRight = colVt - 2 - moneyCellPad
  const colVtTextRight = M + innerW - moneyCellPad
  const rowH = 13
  const headH = 16
  const tableBottomMin = M + 120

  const verticals = (pg, y0, h) => {
    const xs = [colPart - 2, colDesc - 2, colVu - 2, colVt - 2]
    for (const vx of xs) {
      pg.drawLine({
        start: { x: vx, y: y0 },
        end: { x: vx, y: y0 - h },
        thickness: 0.5,
        color: BLACK
      })
    }
  }

  const drawTableHeader = (pg, y0) => {
    pg.drawRectangle({
      x: M,
      y: y0 - headH,
      width: innerW,
      height: headH,
      borderColor: BLACK,
      borderWidth: 1
    })
    const ty = y0 - 11
    pg.drawText('Cantidad', { x: colCant + 2, y: ty, size: 7, font: fontBold })
    pg.drawText('Número de Pieza', { x: colPart, y: ty, size: 7, font: fontBold })
    pg.drawText('Descripción de Pieza', { x: colDesc, y: ty, size: 7, font: fontBold })
    const vu = 'Valor unitario'
    pg.drawText(vu, {
      x: colVuTextRight - fontBold.widthOfTextAtSize(vu, 7),
      y: ty,
      size: 7,
      font: fontBold
    })
    const vt = 'Valor Total'
    pg.drawText(vt, {
      x: colVtTextRight - fontBold.widthOfTextAtSize(vt, 7),
      y: ty,
      size: 7,
      font: fontBold
    })
    verticals(pg, y0, headH)
    return y0 - headH
  }

  const drawRow = (pg, r, y0) => {
    pg.drawRectangle({
      x: M,
      y: y0 - rowH,
      width: innerW,
      height: rowH,
      borderColor: BLACK,
      borderWidth: 0.5
    })
    verticals(pg, y0, rowH)
    const ty = y0 - 9
    const cStr = String(r.cantidad)
    pg.drawText(cStr, {
      x: colCant + 18 - font.widthOfTextAtSize(cStr, 7) / 2,
      y: ty,
      size: 7,
      font
    })
    const np = r.numero_parte.length > 12 ? `${r.numero_parte.slice(0, 10)}…` : r.numero_parte
    pg.drawText(np, { x: colPart + 2, y: ty, size: 7, font })
    const descShort =
      r.descripcion.length > 48 ? `${r.descripcion.slice(0, 46)}…` : r.descripcion
    pg.drawText(descShort, { x: colDesc + 2, y: ty, size: 7, font })
    const su = formatArsPdf(r.precio_unit)
    pg.drawText(su, {
      x: colVuTextRight - font.widthOfTextAtSize(su, 7),
      y: ty,
      size: 7,
      font
    })
    const st = formatArsPdf(r.subtotal)
    pg.drawText(st, {
      x: colVtTextRight - font.widthOfTextAtSize(st, 7),
      y: ty,
      size: 7,
      font
    })
    return y0 - rowH
  }

  let tableY = c.y
  tableY = drawTableHeader(c.page, tableY)

  for (const r of repuestos) {
    if (tableY - rowH < tableBottomMin) {
      c.page = pdfDoc.addPage([A4_W, A4_H])
      tableY = A4_H - M
      tableY = drawTableHeader(c.page, tableY)
    }
    tableY = drawRow(c.page, r, tableY)
  }

  const emptyRows = Math.min(3, Math.max(0, Math.floor((tableY - tableBottomMin) / rowH) - 2))
  for (let i = 0; i < emptyRows; i += 1) {
    if (tableY - rowH < tableBottomMin) break
    c.page.drawRectangle({
      x: M,
      y: tableY - rowH,
      width: innerW,
      height: rowH,
      borderColor: BLACK,
      borderWidth: 0.5
    })
    verticals(c.page, tableY, rowH)
    tableY -= rowH
  }

  c.page.drawLine({
    start: { x: M, y: tableY },
    end: { x: M + innerW, y: tableY },
    thickness: 1,
    color: BLACK
  })
  tableY -= 8

  c.page.drawText('TOTAL ( ARS )', {
    x: colDesc + 2,
    y: tableY,
    size: 8,
    font: fontBold
  })
  c.page.drawText('(colocar la moneda)', {
    x: colDesc + 2,
    y: tableY - 10,
    size: 6,
    font,
    color: rgb(0.35, 0.35, 0.35)
  })
  const t1 = formatArsPdf(totalMonto)
  const t2 = formatArsPdf(totalMonto)
  c.page.drawText(t1, {
    x: colVuTextRight - font.widthOfTextAtSize(t1, 8),
    y: tableY,
    size: 8,
    font: fontBold
  })
  const x2 = colVtTextRight - font.widthOfTextAtSize(t2, 8)
  c.page.drawText(t2, { x: x2, y: tableY, size: 8, font: fontBold })
  const uly = tableY - 3
  const x2e = x2 + font.widthOfTextAtSize(t2, 8) + 2
  c.page.drawLine({
    start: { x: x2 - 2, y: uly },
    end: { x: x2e, y: uly },
    thickness: 0.6,
    color: BLACK
  })
  c.page.drawLine({
    start: { x: x2 - 2, y: uly - 2 },
    end: { x: x2e, y: uly - 2 },
    thickness: 0.6,
    color: BLACK
  })

  c.setY(tableY - 36)

  const duplicar =
    'Duplicar la hoja en caso de necesitar colocar mayor información'
  const drawDuplicar = (pg, y0) => {
    const sz = 7
    const w = font.widthOfTextAtSize(duplicar, sz)
    pg.drawText(duplicar, {
      x: (A4_W - w) / 2,
      y: y0,
      size: sz,
      font,
      color: rgb(0.4, 0.4, 0.4)
    })
    return y0 - 12
  }

  try {
    const pngR = await embedRemoteImage(pdfDoc, assetUrl('resumen_del_diagnostico_descripcion_del_caso.png'))
    const scaleR = innerW / pngR.width
    const hR = pngR.height * scaleR
    c.ensure(hR + 14)
    c.page.drawImage(pngR, { x: M, y: c.y - hR, width: innerW, height: hR })
    c.bump(hR + 8)
  } catch {
    c.ensure(28)
    drawGrayBar(
      c.page,
      M,
      c.y,
      innerW,
      22,
      fontBold,
      'Resumen del diagnóstico / descripción del caso',
      false
    )
    c.bump(28)
  }

  const textBoxPad = 8
  const textMaxW = innerW - 2 * textBoxPad
  const resLines = wrapLines(resumenDiagnostico, font, 9, textMaxW)
  const lineH = 11
  const minBoxH = 60

  let lineIdx = 0
  while (lineIdx < resLines.length) {
    const remaining = resLines.length - lineIdx
    const maxLinesThisPage = Math.max(1, Math.floor((c.y - M - 2 * textBoxPad) / lineH))
    const chunk = resLines.slice(lineIdx, lineIdx + maxLinesThisPage)
    lineIdx += chunk.length
    const boxH = Math.max(minBoxH, chunk.length * lineH + 2 * textBoxPad)
    c.ensure(boxH + 8)
    c.page.drawRectangle({
      x: M,
      y: c.y - boxH,
      width: innerW,
      height: boxH,
      borderColor: BLACK,
      borderWidth: 1
    })
    let ly = c.y - textBoxPad - 9
    for (const ln of chunk) {
      c.page.drawText(ln, { x: M + textBoxPad, y: ly, size: 9, font, color: BLACK })
      ly -= lineH
    }
    c.bump(boxH + 10)
    if (lineIdx < resLines.length) {
      c.page = pdfDoc.addPage([A4_W, A4_H])
      c.setY(A4_H - M)
    }
  }

  async function sectionWithImages(
    fileName,
    titleFallback,
    titleCentered,
    types,
    { frameHMax = 720, forzarAnchoCompleto = true } = {}
  ) {
    const list = []
    for (const t of types) {
      list.push(...filterGallery(galleryImages, t))
    }
    if (!list.length && !fileName && !titleFallback) return

    const usableHGalleryPage = A4_H - 2 * M - 24
    const pageCapH = Math.min(frameHMax, usableHGalleryPage)

    const DUPLICAR_DROP = 16
    /** Tras banner: margen + ensure/borde de la1.ª losa (equiv. a ~M + slab + 20 con duplicar+barra ya descontados). */
    const FIRST_IMAGE_BLOCK_PAD = 36

    let bannerPre = null
    let hBanner = 22
    if (fileName) {
      try {
        bannerPre = await embedRemoteImage(pdfDoc, assetUrl(fileName))
        hBanner = (bannerPre.height / bannerPre.width) * innerW
      } catch {
        bannerPre = null
        hBanner = 22
      }
    } else if (titleFallback) {
      hBanner = 22
    }

    let firstSlabH = 0
    if (list.length) {
      const first = list.find((item) => item?.url)
      if (first?.url) {
        try {
          const b = await fetchBytes(first.url)
          firstSlabH = await measureFirstGallerySlabHeight(
            b,
            first.url,
            innerW,
            pageCapH,
            forzarAnchoCompleto
          )
        } catch {
          firstSlabH = Math.min(pageCapH, 280)
        }
      }
    }

    const needMin =
      DUPLICAR_DROP +
      hBanner +
      2 +
      (list.length ? firstSlabH + FIRST_IMAGE_BLOCK_PAD : 8)
    if (c.y - M < needMin) {
      c.page = pdfDoc.addPage([A4_W, A4_H])
      c.setY(A4_H - M)
    }

    c.setY(drawDuplicar(c.page, c.y - 4))

    if (fileName) {
      await drawAssetBanner(fileName, titleFallback, titleCentered, bannerPre)
    } else if (titleFallback) {
      c.ensure(28)
      drawGrayBar(c.page, M, c.y, innerW, 22, fontBold, titleFallback, !!titleCentered)
      c.bump(28)
    }

    for (const item of list) {
      const url = item?.url
      if (!url) continue
      try {
        const bytes = await fetchBytes(url)
        const img = await embedPdfImageFromBytes(pdfDoc, bytes, url)
        const iw0 = img.width
        const ih0 = img.height
        if (iw0 < 1 || ih0 < 1) continue

        const maxBoxW = (forzarAnchoCompleto ? innerW : innerW - 8) * GALLERY_FIT_PAD
        let maxHDisponible =
          Math.min(pageCapH, c.y - M - 40) * GALLERY_FIT_PAD
        while (maxHDisponible < 72) {
          c.page = pdfDoc.addPage([A4_W, A4_H])
          c.setY(A4_H - M)
          maxHDisponible = Math.min(pageCapH, c.y - M - 40) * GALLERY_FIT_PAD
        }
        let scale = Math.min(maxBoxW / iw0, maxHDisponible / ih0)
        let dw = iw0 * scale
        let dh = ih0 * scale
        c.ensure(dh + 24)
        if (c.y - dh < M + 20) {
          c.page = pdfDoc.addPage([A4_W, A4_H])
          c.setY(A4_H - M)
          maxHDisponible = Math.min(pageCapH, c.y - M - 40) * GALLERY_FIT_PAD
          scale = Math.min(maxBoxW / iw0, maxHDisponible / ih0)
          dw = iw0 * scale
          dh = ih0 * scale
        }
        const bx = M + (innerW - dw) / 2
        c.page.drawRectangle({
          x: bx - 1,
          y: c.y - dh - 3,
          width: dw + 2,
          height: dh + 2,
          borderColor: BLACK,
          borderWidth: 1
        })
        c.page.drawImage(img, { x: bx, y: c.y - dh - 2, width: dw, height: dh })
        c.bump(dh + 16)
      } catch (e) {
        console.warn('pdfProforma: omitiendo imagen', url, e)
      }
    }
  }

  const usableHGalleryPageOuter = A4_H - 2 * M - 24

  if (inc.odometer) {
    await sectionWithImages('odometro.png', 'Odómetro', true, ['ODOMETRO'], {
      frameHMax: usableHGalleryPageOuter
    })
  }
  if (inc.vin) {
    await sectionWithImages('imagen_de_vin.png', 'Imagen de VIN', true, ['VIN'], {
      frameHMax: usableHGalleryPageOuter
    })
  }
  if (inc.patente) {
    await sectionWithImages(
      'dominio_patente.png',
      'Dominio / Patente / Matrícula del vehículo (OPCIONAL)',
      true,
      ['PATENTE'],
      { frameHMax: usableHGalleryPageOuter }
    )
  }
  if (inc.historicoGwm) {
    await sectionWithImages(
      'informacion_de_vehiculo.png',
      'Información de vehículo – histórico GWM',
      true,
      ['HISTORICO_GWM'],
      { frameHMax: usableHGalleryPageOuter }
    )
  }
  if (inc.cat) {
    await sectionWithImages(
      'Informacion_del_caso_consulta_cat_diagnostico_o_sugerencia.png',
      'Información del caso – consulta CAT/diagnostico o sugerencia',
      false,
      ['CAT', 'CONSULTA_CAT'],
      { frameHMax: usableHGalleryPageOuter }
    )
  }
  if (inc.falla) {
    await sectionWithImages(
      'imagen_de_piezas_falla_reparacion_a_realizar.png',
      'Imagen de Piezas – Falla/Reparación a realizar',
      false,
      ['FALLA', 'DANO'],
      { frameHMax: usableHGalleryPageOuter, forzarAnchoCompleto: false }
    )
  }

  const bytes = await pdfDoc.save()
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength)
}
